import dayjs from "dayjs";
import "dayjs/locale/sv";
import React from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../../ui/dialog";
import { ReportDashboard } from "../../../models/ReportBuilder";
import { useStoredDashboards } from "../../../networking/report-dashboards.hooks";
import { useAuthStore } from "../../../stores/auth.store";

interface DashboardInfoDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	dashboard: ReportDashboard;
}

const when = (value?: string): string =>
	value ? dayjs(value).locale("sv").format("D MMMM YYYY, HH:mm") : "–";

/**
 * What the dashboard is, rather than what it shows: who made it and how fresh
 * it is. The two server-kept facts — the owner's name and the time of the last
 * saved change — are read from the dashboards query rather than carried in the
 * store, so they cannot drift into the document that gets pushed back up.
 */
const DashboardInfoDialog: React.FC<DashboardInfoDialogProps> = ({
	open,
	onOpenChange,
	dashboard,
}) => {
	const myName = useAuthStore((s) => s.loggedInUserName);
	const { data } = useStoredDashboards(open);

	const stored = data?.own.find((d) => d.id === dashboard.id);
	const summary = data?.shared.find((d) => d.id === dashboard.id);
	const updatedAt = stored?.updatedAt ?? summary?.updatedAt;

	const createdBy = dashboard.owner
		? dashboard.owner.name ?? "En kollega"
		: stored?.ownerName ?? myName ?? "Du";

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Information</DialogTitle>
					<DialogDescription>{dashboard.name}</DialogDescription>
				</DialogHeader>
				<dl className="divide-y divide-gray-100 text-sm">
					<Row label="Skapad av" value={createdBy} />
					<Row label="Skapad" value={when(dashboard.created)} />
					<Row label="Senast ändrad" value={when(updatedAt)} />
				</dl>
			</DialogContent>
		</Dialog>
	);
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
	<div className="flex items-baseline justify-between gap-4 py-2">
		<dt className="shrink-0 text-gray-500">{label}</dt>
		<dd className="min-w-0 truncate text-right font-medium text-gray-900">{value}</dd>
	</div>
);

export default DashboardInfoDialog;
